// GoLiveTracker.jsx - Pulls from persistent journal, shows accurate day-by-day results
import React, { useState, useEffect } from 'react';

const API = window.location.origin;

export default function GoLiveTracker({ C }) {
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
    const t = setInterval(loadData, 60000);
    return () => clearInterval(t);
  }, []);

  async function loadData() {
    try {
      const r = await fetch(API + '/api/markov/journal');
      const j = await r.json();
      const trades = j.trades || [];

      // Group by date
      const byDate = {};
      trades.forEach(t => {
        const date = t.date || (t.entry_time || '').slice(0, 10);
        if (!date) return;
        if (!byDate[date]) byDate[date] = { trades: 0, wins: 0, pnl: 0 };
        byDate[date].trades++;
        if (t.result === 'WIN') byDate[date].wins++;
        byDate[date].pnl += t.pnl || 0;
      });

      // Sort dates, take last 5 trading days
      const sorted = Object.entries(byDate)
        .sort(([a], [b]) => a.localeCompare(b))
        .slice(-5)
        .map(([date, s]) => ({
          date,
          trades: s.trades,
          wins: s.wins,
          wr: s.trades > 0 ? Math.round(s.wins / s.trades * 100) : 0,
          pnl: s.pnl,
          passed: s.trades >= 3 && (s.wins / s.trades) >= 0.75
        }));

      setDays(sorted);
    } catch (e) {
      console.error('GoLiveTracker error:', e);
    } finally {
      setLoading(false);
    }
  }

  const qualifyingDays = days.filter(d => d.passed).length;
  const needed = 5 - qualifyingDays;
  const isReady = qualifyingDays >= 5;

  const C2 = C || {
    panel: '#0d1526', border: 'rgba(0,212,255,0.12)', accent: '#00D4FF',
    green: '#00d464', red: '#ff4444', amber: '#f59e0b', muted: '#4a6080', text: '#e8eaf0'
  };

  return (
    <div style={{ background: C2.panel, border: `1px solid ${C2.border}`, borderRadius: 12, padding: '14px 16px', marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontSize: 9, color: C2.muted, letterSpacing: 2, fontFamily: 'monospace', textTransform: 'uppercase' }}>
          GO-LIVE TRACKER — 75%+ WR × 5 DAYS × 3+ TRADES
        </div>
        <div style={{ fontSize: 9, color: isReady ? C2.green : C2.amber, fontFamily: 'monospace', fontWeight: 700 }}>
          {qualifyingDays}/5 DAYS
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 6 }}>
        {[0, 1, 2, 3, 4].map(i => {
          const day = days[i];
          const label = `D${i + 1}`;
          if (!day) {
            return (
              <div key={i} style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${C2.border}`, borderRadius: 8, padding: '8px 6px', textAlign: 'center' }}>
                <div style={{ fontSize: 8, color: C2.muted, fontFamily: 'monospace', marginBottom: 4 }}>{label}</div>
                <div style={{ fontSize: 10, color: C2.muted }}>--</div>
              </div>
            );
          }
          const color = day.passed ? C2.green : C2.red;
          return (
            <div key={i} style={{ background: day.passed ? `${C2.green}12` : 'rgba(255,255,255,0.03)', border: `1px solid ${day.passed ? C2.green : C2.border}`, borderRadius: 8, padding: '8px 6px', textAlign: 'center' }}>
              <div style={{ fontSize: 8, color: C2.muted, fontFamily: 'monospace', marginBottom: 2 }}>{label}</div>
              <div style={{ fontSize: 13, color, fontWeight: 700, fontFamily: 'monospace' }}>{day.wr}%</div>
              <div style={{ fontSize: 8, color: C2.muted, fontFamily: 'monospace' }}>{day.trades}T</div>
              <div style={{ fontSize: 8, color: day.pnl >= 0 ? C2.green : C2.red, fontFamily: 'monospace' }}>
                {day.pnl >= 0 ? '+' : ''}{day.pnl.toFixed(0)}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 10, height: 3, background: 'rgba(255,255,255,0.05)', borderRadius: 2 }}>
        <div style={{ height: '100%', width: `${(qualifyingDays / 5) * 100}%`, background: isReady ? C2.green : C2.accent, borderRadius: 2, transition: 'width 0.5s' }} />
      </div>

      <div style={{ marginTop: 6, fontSize: 9, color: isReady ? C2.green : C2.muted, fontFamily: 'monospace', textAlign: 'center' }}>
        {isReady ? '✅ READY TO GO LIVE' : `${needed} more qualifying ${needed === 1 ? 'day' : 'days'} needed`}
      </div>
    </div>
  );
}
